import {
  DEFAULT_MEAL_SLOTS,
  type MealSlot,
  type NutritionGoal,
  type TrainingTimeOfDay,
} from '@cadencia/contracts';
import { normalizeMealSlots } from '@cadencia/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { leerPreferencias, type EstadoPreferencias } from './preferenciaIA.js';

/**
 * El esquema de comidas del usuario, leido de un solo sitio.
 *
 * MISMO PROBLEMA QUE `preferenciaIA.ts`, MISMA GUARDA (NEW5-002)
 *
 * `user_preferences` no existe hasta que alguien la escribe, asi que «no hay
 * fila» significa dos cosas distintas: un usuario que nunca ha configurado
 * nada, donde corresponde el defecto de cuatro comidas, y un dispositivo recien
 * estrenado cuya primera bajada todavia no ha llegado, donde la fila puede
 * existir en el servidor diciendo tres. Mirando solo a Dexie son
 * indistinguibles.
 *
 * Aqui las consecuencias son distintas de las de la IA y en un sentido peor: no
 * se gasta dinero, se genera **un menu semanal entero** con las comidas
 * equivocadas, se escribe en la base y despues hay que rehacerlo. Por eso
 * `comidasConocidas` es `false` mientras no se sepa, y el servicio de nutricion
 * se niega a generar en vez de suponer.
 *
 * Se reutiliza `leerPreferencias` en vez de escribir otra consulta: es la MISMA
 * pregunta —¿sabemos ya lo que hay configurado?— y dos implementaciones de eso
 * acabarian contestando distinto.
 */
export interface EstadoComidas {
  /** Las comidas elegidas, ya en el orden del dia. */
  slots: MealSlot[];
  /** Turno habitual de entrenamiento. */
  defaultTrainingTimeOfDay: TrainingTimeOfDay;
  /**
   * El objetivo de la alimentacion que eligio el usuario. `null` = sin elegir,
   * y entonces no se calcula ningun objetivo: no hay defecto que imponga un
   * deficit o un superavit. Mientras `conocida` sea `false` tambien es `null`,
   * y por la misma razon: no se sabe todavia, asi que no se supone.
   */
  nutritionGoal: NutritionGoal | null;
  /** `false` mientras «no hay fila» pueda significar «no la he mirado». */
  conocida: boolean;
}

/** Lo que corresponde cuando de verdad no hay nada configurado. */
export const COMIDAS_POR_DEFECTO: EstadoComidas = {
  slots: [...DEFAULT_MEAL_SLOTS],
  defaultTrainingTimeOfDay: 'afternoon',
  nutritionGoal: null,
  conocida: true,
};

/**
 * Traduce una fila de preferencias —o su ausencia— al esquema de comidas.
 *
 * Se normaliza el ORDEN al leer, ademas de al escribir. Una fila que llegue de
 * otro dispositivo, de una importacion o de una version anterior puede traer
 * las franjas en cualquier orden, y el reparto por comida depende de cual va
 * antes que cual.
 */
export function comidasDe(estado: EstadoPreferencias): EstadoComidas {
  if (!estado.conocida) {
    return { ...COMIDAS_POR_DEFECTO, conocida: false };
  }
  const fila = estado.fila;
  if (fila === null) return { ...COMIDAS_POR_DEFECTO };

  const slots = normalizeMealSlots(fila.mealSlots);
  return {
    // Una fila con `mealSlots` vacio no deberia existir —lo impiden el schema y
    // el CHECK— pero si llegara, un dia sin comidas no se puede repartir y el
    // motor lanzaria. El defecto es mejor respuesta que una excepcion.
    slots: slots.length > 0 ? slots : [...DEFAULT_MEAL_SLOTS],
    defaultTrainingTimeOfDay: fila.defaultTrainingTimeOfDay,
    nutritionGoal: fila.nutritionGoal,
    conocida: true,
  };
}

export async function leerComidas(userId: string | null): Promise<EstadoComidas> {
  return comidasDe(await leerPreferencias(userId));
}

/**
 * Lo mismo, en vivo. `undefined` mientras la consulta no ha vuelto: quien lo
 * use tiene que distinguir eso de un resultado.
 */
export function useComidas(userId: string | null): EstadoComidas | undefined {
  return useLiveQuery(async () => leerComidas(userId), [userId]);
}
